import {mergeStateObject} from "../../utils/merger";
import React, {useEffect, useRef, useState} from "react";
import {
	Layout, Row, Col, Card, Space, Typography, Button, Input, Select,
	Divider, Tooltip, Badge, Spin, Pagination, notification
} from "antd";
import {
	PlusOutlined,
	ReloadOutlined,
} from "@ant-design/icons";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {fetchWrapper} from "../../helpers";
import {message} from "antd";

const sections = [
'home-page',
'profile-page',
'content-plan',
'publication',
'personal-assistant',
'personal-branding',
];

const formDefaultValues = {
	section: 'home-page',
	message: '',
	files: [],
};

const baseUrl = `${process.env.REACT_APP_API_URL}`;

const SupportForm = () => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const fileInputRef = useRef(null);
	const navigate = useNavigate();
	const { t, i18n } = useTranslation();
	const [files, setFiles] = useState([]);
	const [supportForm, setSupportForm] = useState(formDefaultValues);

	const handleSubmitForm = async (e) => {
		e.preventDefault();
		if (!supportForm.message) return;
		setIsSubmitting(true);

		const formData = new FormData();
		formData.append('section', supportForm.section);
		formData.append('message', supportForm.message);
		if (supportForm.files.length > 0) {
			supportForm.files.forEach((file) => formData.append('files', file));
		}
		fetchWrapper.post(`${baseUrl}/support`, formData).then(response => {
			if (response.message) {
				message.success(t("functions.successfullyGenerated"));
				navigate("/home");
			}
		}).catch((err)=>{
			console.error(err)
			message.error(t("functions.wrong"));
		}).finally(()=>{
			setIsSubmitting(false);
		});
	};

	const handleFileChange = (e) => {
		if (e.target.files && e.target.files.length > 0) {
			setFiles((prevFiles) => {
				const newFiles = Array.from(e.target.files);
				const totalFiles = [...prevFiles, ...newFiles];
				return totalFiles.slice(0, 6);
			});
		}
	};

	const handleDeleteFile = (index) => {
		setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
	}
	const handleResetForm = ()=>{
		setSupportForm(formDefaultValues)
	};

	useEffect(() => {
		mergeStateObject(setSupportForm, {files});
	}, [files]);

	return (
		<Card bordered={false} className="rounded-2xl">
			<form onSubmit={handleSubmitForm}>
				<Space direction="vertical" size={16} className="w-full">
					{/* Section */}
					<div>
						<Typography.Text strong>{t("support.sectionTitle")}</Typography.Text>
						<div className="mt-2">
							<Select
								disabled={isSubmitting}
								value={supportForm.section}
								onChange={(v) => mergeStateObject(setSupportForm, { section: v })}
								className="min-w-[220px] w-full"
								options={sections.map((s) => ({ value: s, label: s.replace("-", " ") }))}
							/>
						</div>
					</div>

					{/* Message */}
					<div>
						<Typography.Text strong>{t("support.describeTitle")}</Typography.Text>
						<Input.TextArea
							required
							disabled={isSubmitting}
							id="message"
							className="mt-2"
							placeholder={t("support.placeholderDescribe")}
							value={supportForm.message}
							onChange={(e) => mergeStateObject(setSupportForm, { message: e.target.value })}
							autoSize={{ minRows: 5, maxRows: 12 }}
							maxLength={2000}
							showCount
						/>
					</div>

					{/* Attachments */}
					<div>
						<Typography.Text strong>{t("support.attachmentTitle")}</Typography.Text>
						<div
							className={`mt-2 border-2 border-dashed rounded-xl p-4 cursor-pointer transition ${
								files.length ? "border-violet-300 bg-violet-50/40" : "border-gray-300 hover:border-violet-400"
							}`}
							onClick={() => fileInputRef.current?.click()}
						>
							{files.length === 0 ? (
								<Space direction="vertical" className="w-full items-center">
									<Typography.Text type="secondary">
                                        {t("support.dropzone.attachment")}
									</Typography.Text>
									<Button type="default" size="small">{t("support.buttons.chooseFiles")}</Button>
								</Space>
							) : (
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
									{files.map((file, index) => (
										<Card
											key={index}
											size="small"
											className="rounded-lg overflow-hidden"
											bodyStyle={{ padding: 8 }}
											actions={[
												<Button
													key="remove"
													danger
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														handleDeleteFile(index);
													}}
												>
                                                    {t("support.buttons.remove")}
												</Button>,
											]}
										>
											<div className="w-full">
												{file.type.includes("image") ? (
													<img
														className="mx-auto rounded-md max-h-48 object-contain"
														src={URL.createObjectURL(file)}
														alt={file.name}
													/>
												) : (
													<video className="mx-auto rounded-md max-h-48" src={URL.createObjectURL(file)} controls />
												)}
												<Typography.Paragraph className="text-xs mt-2 mb-0 truncate">{file.name}</Typography.Paragraph>
											</div>
										</Card>
									))}
								</div>
							)}
						</div>

						{/* Hidden native input (kept logic intact) */}
						<input
							multiple
							type="file"
							id="fileInput"
							className="hidden"
							ref={fileInputRef}
							onChange={handleFileChange}
							accept="video/mp4, video/mov, video/wmv, video/avi, video/flv, video/mpeg, video/mpg, video/quicktime, video/3gpp, video/webm, image/jpeg, image/png, image/jpg"
						/>
					</div>

					{/* Actions */}
					<Space className="w-full justify-end">
						<Button
							disabled={isSubmitting}
							onClick={handleResetForm}
							htmlType="reset"
							className="!rounded-lg"
						>
                            {t("support.buttons.clear")}
						</Button>
						<Button
							type="primary"
							htmlType="submit"
							loading={isSubmitting}
							className="!rounded-lg"
						>
                            {t("support.buttons.send")}
						</Button>
					</Space>
				</Space>
			</form>
		</Card>
	);

};

export default SupportForm;